import React, { useState } from 'react';
import Card from '../common/Card';
import Button from '../common/Button';
import { useReservations } from '../../hooks/useReservations';
import LogoPatternBackground from '../common/LogoPatternBackground';

const ReservationsList = () => {
  const { bookings: reservations, loading, cancelReservation } = useReservations();
  const [cancellingId, setCancellingId] = useState(null);
  
  // Tarih formatını düzenleyen yardımcı fonksiyon
  const formatDate = (dateValue) => {
    let date;
    if (dateValue?.toDate) {
      // Firestore Timestamp
      date = dateValue.toDate();
    } else if (typeof dateValue === 'string') {
      date = new Date(dateValue);
    } else {
      date = dateValue;
    }
    return date?.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' }) || '-';
  };
  
  // Saat formatını düzenleyen yardımcı fonksiyon
  const formatTime = (startTime, endTime) => {
    if (startTime && endTime) {
      return `${startTime} - ${endTime}`;
    }
    return '-';
  };

  // Rezervasyon süresi geçmiş mi kontrol et
  const isExpired = (reservation) => {
    let resDate;
    if (reservation.date?.toDate) {
      resDate = reservation.date.toDate();
    } else if (reservation.dateString) {
      resDate = new Date(reservation.dateString);
    } else {
      resDate = new Date(reservation.date);
    }
    
    // Bitiş saatini ekle
    if (reservation.endTime) {
      const [hours, minutes] = reservation.endTime.split(':').map(Number);
      resDate.setHours(hours, minutes, 0, 0);
    }
    
    return resDate < new Date();
  };

  // Rezervasyon durumunu belirle
  const getReservationStatus = (reservation) => {
    if (reservation.status === 'cancelled') {
      return { label: 'İptal Edildi', className: 'bg-red-600 text-white' };
    }
    if (isExpired(reservation)) {
      return { label: 'Tamamlandı', className: 'bg-gray-600 text-gray-200' };
    }
    return { label: 'Aktif', className: 'bg-green-600 text-white' };
  };
  
  // İptal işlemini yönetecek fonksiyon
  const onCancel = async (id) => {
    if (window.confirm('Bu rezervasyonu iptal etmek istediğinize emin misiniz? Kapı kodunuz da iptal edilecektir.')) {
      setCancellingId(id);
      try {
        await cancelReservation(id);
      } catch (error) {
        alert('Rezervasyon iptal edilirken bir hata oluştu: ' + error.message);
      } finally {
        setCancellingId(null);
      }
    }
  };
  
  if (loading) {
    return (
        <Card title="Rezervasyonlarım" className="fade-in">
            <div className="text-center py-12 slide-up">
                <LogoPatternBackground className="w-32 h-32 mx-auto mb-4 animate-pulse text-blue-400 opacity-50" />
                <p className="text-blue-200 mb-4">Rezervasyonlar yükleniyor...</p>
            </div>
        </Card>
    );
  }

  if (!reservations) {
    return (
        <Card title="Rezervasyonlarım" className="fade-in">
            <div className="text-center py-12 slide-up">
                <p className="text-blue-200 mb-4">Rezervasyonlar yüklenirken bir hata oluştu.</p>
            </div>
        </Card>
    )
  } else if (reservations.length === 0) {
    return (
      <Card title="Rezervasyonlarım" className="fade-in">
        <div className="text-center py-12 slide-up">
          <svg className="w-16 h-16 mx-auto mb-4 text-blue-400 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <p className="text-blue-200 mb-4">Henüz hiç rezervasyonunuz bulunmuyor.</p>
          <Button variant="primary" onClick={() => window.location.href='/reserve'} className="hover-scale">
            Rezervasyon Oluştur
          </Button>
        </div>
      </Card>
    );
  }
  
  return (
    <Card title="Rezervasyonlarım">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-700">
          <thead className="bg-gray-900">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                Tarih
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                Saat
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                PIN Kodu
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                Durum
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-300 uppercase tracking-wider">
                İşlem
              </th>
            </tr>
          </thead>
          <tbody className="bg-gray-800 divide-y divide-gray-700">
            {reservations.map((reservation) => {
              const status = getReservationStatus(reservation);
              const expired = isExpired(reservation);
              
              return (
              <tr key={reservation.id} className={`hover:bg-gray-700 ${expired ? 'opacity-70' : ''}`}>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-100">{formatDate(reservation.date || reservation.dateString)}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-100">{formatTime(reservation.startTime, reservation.endTime)}</div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className={`text-sm font-mono font-medium py-1 px-3 rounded-full inline-block border ${
                    expired 
                      ? 'bg-gray-700 text-gray-400 border-gray-600' 
                      : 'bg-blue-900 text-blue-100 border-blue-700'
                  }`}>
                    {reservation.pin || '-'}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${status.className}`}>
                    {status.label}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  {reservation.status !== 'cancelled' && !expired && (
                    <button 
                      onClick={() => onCancel(reservation.id)}
                      disabled={cancellingId === reservation.id}
                      className={`text-red-300 hover:text-red-100 font-medium bg-red-900 px-3 py-1 rounded ${cancellingId === reservation.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      {cancellingId === reservation.id ? 'İptal Ediliyor...' : 'İptal Et'}
                    </button>
                  )}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
};

export default ReservationsList;